import { useEffect, useState } from 'react';
import { Settings2, Loader2, Plus, Trash2, Save } from 'lucide-react';
import { api, ErrorApi } from '../api';
import Ayuda from '../ui/Ayuda';
import Motivo from '../experiencia/Motivo';

/* =====================================================================
   Configuración de la mesa · DIRECTRIZ-TICKETS F3
   =====================================================================
   Para quien administra la mesa: los plazos de atención por prioridad, el
   horario laboral con que se cuentan, las colas y a qué cola va cada tipo
   de problema, y los días festivos. La mesa funciona sin tocar nada —trae
   valores por omisión sensatos (R-TK5)—; esto es para afinarlos.
   ===================================================================== */

const DIAS = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
const PRIORIDAD = { 1: 'Crítica', 2: 'Alta', 3: 'Media', 4: 'Baja' };

function Seccion({ titulo, children }) {
  return (
    <section className="flex flex-col gap-2 rounded-xl border border-gray-200 p-4">
      <h2 className="font-semibold text-gray-900">{titulo}</h2>
      {children}
    </section>
  );
}

export default function ConfiguracionMesa() {
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');
  const nota = (msg) => { setAviso(msg); setError(''); };
  const falla = (e) => setError(e instanceof ErrorApi ? e.message : 'No se pudo guardar el cambio.');

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-4 p-4">
      <header>
        <h1 className="flex items-center gap-2 text-xl font-semibold text-gray-900">
          <Settings2 className="h-6 w-6" aria-hidden="true" /> Configuración de la mesa
        </h1>
        <p className="text-gray-600">Los plazos, el horario, las colas y los festivos de tu mesa de ayuda.</p>
        <Ayuda id="config_mesa.pantalla" />
      </header>

      {error && <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-800">{error}</p>}
      {aviso && <p className="rounded-lg bg-green-50 p-3 text-sm text-green-800">{aviso}</p>}

      <Plazos onOk={nota} onError={falla} />
      <Horario onOk={nota} onError={falla} />
      <Colas onOk={nota} onError={falla} />
      <Reglas onOk={nota} onError={falla} />
      <Festivos onOk={nota} onError={falla} />
    </div>
  );
}

function Plazos({ onOk, onError }) {
  const [filas, setFilas] = useState(null);
  useEffect(() => {
    api.get('/tickets/config/sla').then(setFilas).catch(() => setFilas([]));
  }, []);
  function cambiar(i, campo, valor) {
    const copia = filas.map((f, j) => (j === i ? { ...f, [campo]: valor } : f));
    setFilas(copia);
  }
  async function guardar(f) {
    try {
      await api.put('/tickets/config/sla', {
        prioridad: f.prioridad,
        min_primera_respuesta: Number(f.min_primera_respuesta),
        min_resolucion: Number(f.min_resolucion),
      });
      onOk('Plazos guardados.');
    } catch (e) { onError(e); }
  }
  return (
    <Seccion titulo="Plazos de atención por prioridad">
      <Ayuda id="config_mesa.plazos" />
      {filas === null && <Loader2 className="h-4 w-4 animate-spin text-gray-400" aria-hidden="true" />}
      {filas && filas.map((f, i) => (
        <div key={f.prioridad} className="flex flex-wrap items-end gap-2">
          <span className="w-20 text-sm font-medium text-gray-800">{PRIORIDAD[f.prioridad]}</span>
          <label className="text-xs text-gray-600">Primera respuesta (min)
            <input inputMode="numeric" value={f.min_primera_respuesta}
                   onChange={(e) => cambiar(i, 'min_primera_respuesta', e.target.value)}
                   className="mt-1 block min-h-[44px] w-28 rounded-lg border border-gray-300 px-2 text-base" />
          </label>
          <label className="text-xs text-gray-600">Resolución (min)
            <input inputMode="numeric" value={f.min_resolucion}
                   onChange={(e) => cambiar(i, 'min_resolucion', e.target.value)}
                   className="mt-1 block min-h-[44px] w-28 rounded-lg border border-gray-300 px-2 text-base" />
          </label>
          <button type="button" onClick={() => guardar(f)}
                  className="flex min-h-[44px] items-center gap-1 rounded-lg bg-[var(--color-marca)] px-3 text-sm font-semibold text-white">
            <Save className="h-4 w-4" aria-hidden="true" /> Guardar
          </button>
        </div>
      ))}
    </Seccion>
  );
}

function Horario({ onOk, onError }) {
  const [filas, setFilas] = useState(null);
  function recargar() {
    api.get('/tickets/config/horario').then(setFilas).catch(() => setFilas([]));
  }
  useEffect(recargar, []);
  const porDia = (d) => (filas || []).find((f) => f.dia_semana === d);
  async function guardar(d, abre, cierra) {
    try {
      await api.put('/tickets/config/horario', { dia_semana: d, abre, cierra });
      onOk('Horario guardado.'); recargar();
    } catch (e) { onError(e); }
  }
  async function cerrar(d) {
    try { await api.delete(`/tickets/config/horario/${d}`); onOk('Día marcado como cerrado.'); recargar(); }
    catch (e) { onError(e); }
  }
  return (
    <Seccion titulo="Horario laboral">
      <Ayuda id="config_mesa.horario" />
      {filas === null && <Loader2 className="h-4 w-4 animate-spin text-gray-400" aria-hidden="true" />}
      {filas && DIAS.map((nombre, d) => {
        const h = porDia(d);
        return (
          <div key={d} className="flex flex-wrap items-center gap-2">
            <span className="w-24 text-sm text-gray-800">{nombre}</span>
            <input type="time" defaultValue={h?.abre || '09:00'}
                   id={`abre-${d}`} className="min-h-[44px] rounded-lg border border-gray-300 px-2 text-base" />
            <span className="text-gray-500">a</span>
            <input type="time" defaultValue={h?.cierra || '18:00'}
                   id={`cierra-${d}`} className="min-h-[44px] rounded-lg border border-gray-300 px-2 text-base" />
            <button type="button"
                    onClick={() => guardar(d,
                      document.getElementById(`abre-${d}`).value,
                      document.getElementById(`cierra-${d}`).value)}
                    className="min-h-[44px] rounded-lg bg-[var(--color-marca)] px-3 text-sm font-semibold text-white">
              {h ? 'Guardar' : 'Abrir este día'}
            </button>
            {h && (
              <button type="button" onClick={() => cerrar(d)}
                      className="min-h-[44px] rounded-lg border border-gray-300 px-3 text-sm text-gray-700">
                Cerrar este día
              </button>
            )}
          </div>
        );
      })}
    </Seccion>
  );
}

function Colas({ onOk, onError }) {
  const [colas, setColas] = useState(null);
  const [tecnicos, setTecnicos] = useState([]);
  const [nombre, setNombre] = useState('');
  function recargar() { api.get('/tickets/colas').then(setColas).catch(() => setColas([])); }
  useEffect(recargar, []);
  useEffect(() => { api.get('/tickets/tecnicos').then(setTecnicos).catch(() => setTecnicos([])); }, []);
  async function crear() {
    if (!nombre.trim()) return;
    try { await api.post('/tickets/colas', { nombre: nombre.trim() }); setNombre(''); onOk('Cola creada.'); recargar(); }
    catch (e) { onError(e); }
  }
  async function baja(c) {
    try { await api.post(`/tickets/colas/${c.id}/baja`, {}); onOk('Cola dada de baja.'); recargar(); }
    catch (e) { onError(e); }
  }
  async function agregar(c, id_usuario) {
    if (!id_usuario) return;
    try { await api.post(`/tickets/colas/${c.id}/miembros`, { id_usuario: Number(id_usuario) }); onOk('Técnico agregado a la cola.'); recargar(); }
    catch (e) { onError(e); }
  }
  return (
    <Seccion titulo="Colas y técnicos">
      <div className="flex flex-wrap gap-2">
        <input value={nombre} onChange={(e) => setNombre(e.target.value)}
               placeholder="Nombre de la cola (ej. Soporte TI)"
               className="min-h-[44px] flex-1 rounded-lg border border-gray-300 px-3 text-base" />
        {/* P13 (29 sep 2026): con el nombre vacío el botón no hacía nada y
            el censo lo contaba «sin reacción». Ahora se apaga y dice qué
            falta (Doctrina 2, regla 8). */}
        <button type="button" onClick={crear} disabled={!nombre.trim()}
                aria-describedby={!nombre.trim() ? 'motivo-cola-nueva' : undefined}
                className="flex min-h-[44px] items-center gap-1 rounded-lg bg-[var(--color-marca)] px-3 text-sm font-semibold text-white disabled:opacity-50">
          <Plus className="h-4 w-4" aria-hidden="true" /> Crear cola
        </button>
      </div>
      <Motivo id="motivo-cola-nueva" cuando={!nombre.trim()}>
        Escribe el nombre de la cola para poder crearla.
      </Motivo>
      {colas === null && <Loader2 className="h-4 w-4 animate-spin text-gray-400" aria-hidden="true" />}
      {colas && colas.map((c) => (
        <div key={c.id} className="flex flex-wrap items-center gap-2 border-t border-gray-100 pt-2">
          <span className="font-medium text-gray-800">{c.nombre}</span>
          <span className="text-xs text-gray-500">{c.miembros} técnico(s)</span>
          <select defaultValue="" onChange={(e) => agregar(c, e.target.value)}
                  className="min-h-[44px] rounded-lg border border-gray-300 px-2 text-sm">
            <option value="">Agregar técnico…</option>
            {tecnicos.map((u) => <option key={u.id} value={u.id}>{u.nombre_completo}</option>)}
          </select>
          {c.clave !== 'GENERAL' && (
            <button type="button" onClick={() => baja(c)}
                    className="flex min-h-[44px] items-center gap-1 rounded-lg border border-gray-300 px-2 text-sm text-gray-700">
              <Trash2 className="h-4 w-4" aria-hidden="true" /> Baja
            </button>
          )}
        </div>
      ))}
    </Seccion>
  );
}

function Reglas({ onOk, onError }) {
  const [reglas, setReglas] = useState(null);
  const [categorias, setCategorias] = useState([]);
  const [colas, setColas] = useState([]);
  const [nueva, setNueva] = useState({ id_categoria: '', id_cola: '' });
  function recargar() { api.get('/tickets/reglas').then(setReglas).catch(() => setReglas([])); }
  useEffect(recargar, []);
  useEffect(() => {
    api.get('/tickets/categorias').then(setCategorias).catch(() => setCategorias([]));
    api.get('/tickets/colas').then(setColas).catch(() => setColas([]));
  }, []);
  async function crear() {
    if (!nueva.id_cola) return;
    try {
      await api.post('/tickets/reglas', {
        id_cola: Number(nueva.id_cola),
        id_categoria: nueva.id_categoria ? Number(nueva.id_categoria) : null,
      });
      setNueva({ id_categoria: '', id_cola: '' }); onOk('Regla creada.'); recargar();
    } catch (e) { onError(e); }
  }
  async function baja(r) {
    try { await api.post(`/tickets/reglas/${r.id}/baja`, {}); onOk('Regla dada de baja.'); recargar(); }
    catch (e) { onError(e); }
  }
  return (
    <Seccion titulo="A qué cola va cada tipo de problema">
      <div className="flex flex-wrap items-center gap-2">
        <select value={nueva.id_categoria} onChange={(e) => setNueva({ ...nueva, id_categoria: e.target.value })}
                className="min-h-[44px] rounded-lg border border-gray-300 px-2 text-sm">
          <option value="">Cualquier categoría</option>
          {categorias.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
        </select>
        <span className="text-gray-500">va a</span>
        <select value={nueva.id_cola} onChange={(e) => setNueva({ ...nueva, id_cola: e.target.value })}
                className="min-h-[44px] rounded-lg border border-gray-300 px-2 text-sm">
          <option value="">Elige una cola…</option>
          {colas.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
        </select>
        <button type="button" onClick={crear} disabled={!nueva.id_cola}
                aria-describedby={!nueva.id_cola ? 'motivo-regla-nueva' : undefined}
                className="min-h-[44px] rounded-lg bg-[var(--color-marca)] px-3 text-sm font-semibold text-white disabled:opacity-50">
          Agregar regla
        </button>
      </div>
      <Motivo id="motivo-regla-nueva" cuando={!nueva.id_cola}>
        {colas.length === 0
          ? 'Crea primero una cola en «Colas y técnicos»; después eliges aquí a cuál va cada tipo de problema.'
          : 'Elige a qué cola va para poder agregar la regla.'}
      </Motivo>
      {reglas === null && <Loader2 className="h-4 w-4 animate-spin text-gray-400" aria-hidden="true" />}
      {reglas && reglas.map((r) => (
        <div key={r.id} className="flex flex-wrap items-center gap-2 border-t border-gray-100 pt-2 text-sm">
          <span className="text-gray-800">{r.categoria || 'Cualquier categoría'}
            {r.sucursal ? ` · ${r.sucursal}` : ''} → {r.cola}</span>
          <button type="button" onClick={() => baja(r)}
                  className="flex min-h-[44px] items-center gap-1 rounded-lg border border-gray-300 px-2 text-gray-700">
            <Trash2 className="h-4 w-4" aria-hidden="true" /> Baja
          </button>
        </div>
      ))}
    </Seccion>
  );
}

function Festivos({ onOk, onError }) {
  const [lista, setLista] = useState(null);
  const [nuevo, setNuevo] = useState({ fecha: '', nombre: '' });
  function recargar() { api.get('/tickets/config/festivos').then(setLista).catch(() => setLista([])); }
  useEffect(recargar, []);
  async function crear() {
    if (!nuevo.fecha) return;
    try { await api.post('/tickets/config/festivos', nuevo); setNuevo({ fecha: '', nombre: '' }); onOk('Festivo agregado.'); recargar(); }
    catch (e) { onError(e); }
  }
  async function baja(f) {
    try { await api.post(`/tickets/config/festivos/${f.id}/baja`, {}); onOk('Festivo quitado.'); recargar(); }
    catch (e) { onError(e); }
  }
  return (
    <Seccion titulo="Días festivos (no cuentan para el reloj)">
      <div className="flex flex-wrap items-center gap-2">
        <input type="date" value={nuevo.fecha} onChange={(e) => setNuevo({ ...nuevo, fecha: e.target.value })}
               className="min-h-[44px] rounded-lg border border-gray-300 px-2 text-base" />
        <input value={nuevo.nombre} onChange={(e) => setNuevo({ ...nuevo, nombre: e.target.value })}
               placeholder="Nombre (opcional)"
               className="min-h-[44px] flex-1 rounded-lg border border-gray-300 px-3 text-base" />
        <button type="button" onClick={crear} disabled={!nuevo.fecha}
                aria-describedby={!nuevo.fecha ? 'motivo-festivo-nuevo' : undefined}
                className="min-h-[44px] rounded-lg bg-[var(--color-marca)] px-3 text-sm font-semibold text-white disabled:opacity-50">
          Agregar
        </button>
      </div>
      <Motivo id="motivo-festivo-nuevo" cuando={!nuevo.fecha}>
        Elige la fecha del festivo para poder agregarlo.
      </Motivo>
      {lista === null && <Loader2 className="h-4 w-4 animate-spin text-gray-400" aria-hidden="true" />}
      {lista && lista.map((f) => (
        <div key={f.id} className="flex items-center justify-between gap-2 border-t border-gray-100 pt-2 text-sm">
          <span className="text-gray-800">{f.fecha}{f.nombre ? ` · ${f.nombre}` : ''}</span>
          <button type="button" onClick={() => baja(f)}
                  className="flex min-h-[44px] items-center gap-1 rounded-lg border border-gray-300 px-2 text-gray-700">
            <Trash2 className="h-4 w-4" aria-hidden="true" /> Quitar
          </button>
        </div>
      ))}
    </Seccion>
  );
}
